import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@akuna/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { useEntitlements } from "@/hooks/useEntitlements";
import { usePermissionsMap } from "@/hooks/usePermissionsMap";

type RangeKey = "7" | "30" | "90";

type KpiRow = {
  clinic_id: string;
  day: string;
  send_requests: number;
  send_success: number;
  send_errors: number;
  delivered: number;
  read: number;
  failed: number;
};

type FailureRow = {
  clinic_id: string;
  created_at: string;
  wamid: string | null;
  reason: string;
};

export default function WhatsAppAnalytics() {
  const { t } = useTranslation("settings");
  const { data: profile } = useProfile();
  const { data: entitlements, isLoading: isEntitlementsLoading } = useEntitlements();
  const { data: permissionsMap, isLoading: isPermissionsLoading } = usePermissionsMap();
  const clinicId = profile?.current_clinic_id ?? null;
  const [rangeDays, setRangeDays] = useState<RangeKey>("30");
  const hasWhatsAppModule = Boolean(entitlements?.activeModules?.includes("whatsapp"));
  const canConfigureWhatsApp =
    Boolean(permissionsMap?.admin_settings?.update) ||
    Boolean(permissionsMap?.manage_clinic?.update) ||
    Boolean(permissionsMap?.whatsapp_config?.update);
  const canAccessWhatsAppAnalytics = hasWhatsAppModule && canConfigureWhatsApp;

  const sinceDate = useMemo(() => {
    const days = Number(rangeDays);
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  }, [rangeDays]);

  const { data: kpis, isLoading: isLoadingKpis } = useQuery({
    queryKey: ["whatsapp-analytics-kpis", clinicId, rangeDays],
    enabled: !!clinicId && canAccessWhatsAppAnalytics,
    queryFn: async (): Promise<KpiRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("vw_whatsapp_kpis_daily")
        .select("*")
        .eq("clinic_id", clinicId)
        .gte("day", sinceDate)
        .order("day", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as KpiRow[];
    },
    staleTime: 1000 * 30,
  });

  const { data: failures, isLoading: isLoadingFailures } = useQuery({
    queryKey: ["whatsapp-analytics-failures", clinicId, rangeDays],
    enabled: !!clinicId && canAccessWhatsAppAnalytics,
    queryFn: async (): Promise<FailureRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("vw_whatsapp_failures_by_reason")
        .select("clinic_id, created_at, wamid, reason")
        .eq("clinic_id", clinicId)
        .gte("created_at", new Date(sinceDate).toISOString())
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as FailureRow[];
    },
    staleTime: 1000 * 30,
  });

  const totals = useMemo(() => {
    const rows = kpis ?? [];
    return rows.reduce(
      (acc, row) => {
        acc.send_requests += row.send_requests ?? 0;
        acc.send_success += row.send_success ?? 0;
        acc.send_errors += row.send_errors ?? 0;
        acc.delivered += row.delivered ?? 0;
        acc.read += row.read ?? 0;
        acc.failed += row.failed ?? 0;
        return acc;
      },
      { send_requests: 0, send_success: 0, send_errors: 0, delivered: 0, read: 0, failed: 0 }
    );
  }, [kpis]);

  if (isEntitlementsLoading || isPermissionsLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!canAccessWhatsAppAnalytics) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t("whatsappAnalyticsTitle")}</h1>
          <p className="text-muted-foreground">{t("whatsappAnalyticsSubtitle")}</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={rangeDays} onValueChange={(v) => setRangeDays(v as RangeKey)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("whatsappAnalyticsRangePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t("whatsappAnalyticsRange7")}</SelectItem>
              <SelectItem value="30">{t("whatsappAnalyticsRange30")}</SelectItem>
              <SelectItem value="90">{t("whatsappAnalyticsRange90")}</SelectItem>
            </SelectContent>
          </Select>
          <Button asChild variant="outline">
            <Link to="/settings/whatsapp-meta">{t("whatsappAnalyticsSettingsLink")}</Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">{t("whatsappAnalyticsCardSends")}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingKpis ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : null}
            <div className="text-2xl font-bold">{totals.send_requests}</div>
            <div className="text-xs text-muted-foreground">{t("whatsappAnalyticsMetricSendRequest")}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">{t("whatsappAnalyticsCardDeliveredRead")}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingKpis ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : null}
            <div className="text-2xl font-bold">
              {totals.delivered} / {totals.read}
            </div>
            <div className="text-xs text-muted-foreground">{t("whatsappAnalyticsMetricWebhookStatus")}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">{t("whatsappAnalyticsCardFailures")}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingKpis ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : null}
            <div className="text-2xl font-bold">{totals.failed + totals.send_errors}</div>
            <div className="text-xs text-muted-foreground">{t("whatsappAnalyticsMetricFailuresFormula")}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("whatsappAnalyticsKpisTitle")}</CardTitle>
          <CardDescription>{t("whatsappAnalyticsKpisDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoadingKpis ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("loading", { ns: "common" })}
            </div>
          ) : (kpis?.length ?? 0) === 0 ? (
            <div className="text-sm text-muted-foreground">{t("whatsappAnalyticsNoData")}</div>
          ) : (
            <div className="w-full overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left p-2">{t("whatsappAnalyticsTableDay")}</th>
                    <th className="text-right p-2">{t("whatsappAnalyticsTableSends")}</th>
                    <th className="text-right p-2">{t("whatsappAnalyticsTableDelivered")}</th>
                    <th className="text-right p-2">{t("whatsappAnalyticsTableRead")}</th>
                    <th className="text-right p-2">{t("whatsappAnalyticsTableFailures")}</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis?.map((row) => (
                    <tr key={row.day} className="border-t">
                      <td className="p-2">{row.day}</td>
                      <td className="p-2 text-right">{row.send_requests}</td>
                      <td className="p-2 text-right">{row.delivered}</td>
                      <td className="p-2 text-right">{row.read}</td>
                      <td className="p-2 text-right">{row.failed + row.send_errors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("whatsappAnalyticsRecentFailuresTitle")}</CardTitle>
          <CardDescription>{t("whatsappAnalyticsRecentFailuresDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoadingFailures ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("loading", { ns: "common" })}
            </div>
          ) : (failures?.length ?? 0) === 0 ? (
            <div className="text-sm text-muted-foreground">{t("whatsappAnalyticsNoFailures")}</div>
          ) : (
            <div className="space-y-2">
              {failures?.map((row) => (
                <div key={`${row.created_at}:${row.wamid ?? ""}`} className="rounded-md border p-2 text-sm">
                  <div className="text-xs text-muted-foreground">{row.created_at}</div>
                  <div className="font-medium break-all">{row.reason}</div>
                  {row.wamid ? <div className="text-xs text-muted-foreground break-all">{row.wamid}</div> : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
